import { useCallback, useEffect, useState } from 'react';
import {
  createUserPrompt,
  deleteUserPrompt,
  listUserPrompts,
  recordUserPromptUse,
  updateUserPrompt,
} from '../api/userPromptApi';

// 서버 정렬(user_prompt_service._sort_key)과 같은 기준: last_used_at, 없으면 created_at 최신순.
// 서버는 "+00:00", 클라이언트는 "Z" 형식이라 문자열이 아닌 시각으로 비교한다.
const recentUseTime = (prompt) => Date.parse(prompt.last_used_at || prompt.created_at || '') || 0;
export const sortByRecentUse = (prompts) => [...prompts].sort(
  (a, b) => recentUseTime(b) - recentUseTime(a)
);

/**
 * 채팅 "내 프롬프트" 상태 훅.
 *
 * - 로그인(비익명) 사용자만 로드한다. 그 외에는 항상 빈 배열.
 * - 목록은 서버에서 최근 사용순으로 온다. 로컬 변경은 낙관적 업데이트 + 실패 롤백.
 * - 사용 기록(markUsed)은 best-effort: 로컬 반영 후 서버 실패는 무시한다.
 */
const useUserPrompts = (currentUser, isAnonymousUser) => {
  const [prompts, setPrompts] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const enabled = Boolean(currentUser) && !isAnonymousUser;
  const uid = currentUser?.uid || null;

  useEffect(() => {
    if (!enabled) {
      setPrompts([]);
      setIsLoaded(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await listUserPrompts(currentUser);
        if (cancelled) return;
        setPrompts(Array.isArray(data.prompts) ? data.prompts : []);
        setIsLoaded(true);
      } catch (_error) {
        if (cancelled) return;
        // 로드 실패 시 조용히 빈 상태 유지 (칩이 안 보일 뿐, 채팅 흐름을 막지 않는다)
        setPrompts([]);
        setIsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, uid]);

  const createPrompt = useCallback(async ({ body, category, title }) => {
    const data = await createUserPrompt(currentUser, { body, category, title });
    if (data?.prompt) {
      setPrompts((prev) => [data.prompt, ...prev]);
    }
    return data?.prompt;
  }, [currentUser]);

  const updatePrompt = useCallback(async (promptId, fields) => {
    const previous = prompts;
    setPrompts((prev) => prev.map((p) => (
      p.id === promptId ? { ...p, ...fields } : p
    )));
    try {
      const data = await updateUserPrompt(currentUser, promptId, fields);
      if (data?.prompt) {
        setPrompts((prev) => prev.map((p) => (
          p.id === promptId ? data.prompt : p
        )));
      }
      return data?.prompt;
    } catch (error) {
      setPrompts(previous);
      throw error;
    }
  }, [currentUser, prompts]);

  const deletePrompt = useCallback(async (promptId) => {
    const previous = prompts;
    setPrompts((prev) => prev.filter((p) => p.id !== promptId));
    try {
      await deleteUserPrompt(currentUser, promptId);
    } catch (error) {
      setPrompts(previous);
      throw error;
    }
  }, [currentUser, prompts]);

  const markUsed = useCallback((promptId) => {
    const nowIso = new Date().toISOString();
    setPrompts((prev) => sortByRecentUse(prev.map((p) => (
      p.id === promptId
        ? { ...p, usage_count: (p.usage_count || 0) + 1, last_used_at: nowIso }
        : p
    ))));
    recordUserPromptUse(currentUser, promptId).catch(() => {
      // 정렬용 기록이므로 실패는 무시 (사용자 흐름 차단 금지)
    });
  }, [currentUser]);

  return {
    prompts,
    isLoaded,
    enabled,
    createPrompt,
    updatePrompt,
    deletePrompt,
    markUsed,
  };
};

export default useUserPrompts;
